import React, { useState } from 'react';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';

// Counter Component for animations
const Counter = ({ end = 0, duration = 1500, prefix = "", suffix = "" }) => {
  const [val, setVal] = React.useState(0);
  React.useEffect(() => {
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      setVal(Number((end * p).toFixed(1)));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [end, duration]);
  return (
    <span>
      {prefix}
      {val}
      {suffix}
    </span>
  );
};

// Investor Portal Component
const InvestorWelcome = ({ onDismiss, onStartDemo }) => {
  const overlayRef = React.useRef(null);
  const closeBtnRef = React.useRef(null);

  React.useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const focusable = overlay.querySelectorAll(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function onKey(e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onDismiss?.();
      }
      if (e.key === "Tab" && focusable.length > 1) {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", onKey);
    closeBtnRef.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  const Section = ({ children, className = "" }) => (
    <section className={className + " animate-fadeUp"}>{children}</section>
  );

  const PatentBadge = () => (
    <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-amber-300/40 rounded-lg px-2 sm:px-3 py-2">
      <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-amber-300 to-orange-400 rounded-full"></div>
      <div>
        <p className="text-xs font-bold text-white leading-tight">PATENT PENDING</p>
        <p className="text-xs text-blue-100">US 2025</p>
      </div>
    </div>
  );

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-3 md:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="RepMingle investor presentation"
    >
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-zoomIn">
        
        {/* HEADER */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4 sm:p-6 md:p-8 relative">
          <button
            ref={closeBtnRef}
            onClick={onDismiss}
            aria-label="Close investor presentation"
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 hover:bg-white/20 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            ✕
          </button>

          <div className="flex items-start gap-2 sm:gap-3 md:gap-4 mb-2 pr-8">
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16">
              <img 
                src="/logo.png" 
                alt="RepMingle Logo" 
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-4xl font-black leading-tight break-words">RepMingle</h1>
              <p className="text-xs sm:text-sm text-blue-100">Stop Guessing! Start Training!</p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <PatentBadge />
          </div>

          <div className="mt-3 sm:mt-6">
            <h2 className="text-base sm:text-lg md:text-2xl font-bold text-blue-100">
              Welcome, Investor
            </h2>
            <p className="text-xs sm:text-sm text-blue-100/80 mt-1">
              Interactive MVP Demo • Scale Revenue from Idle Capacity
            </p>
          </div>
        </div>

        {/* CONTENT */}
        <div className="p-3 sm:p-4 md:p-8 space-y-4 sm:space-y-5 md:space-y-6">
          
          <Section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6">
            <h2 className="text-base sm:text-lg md:text-2xl font-bold mb-2">Ready to See It in Action?</h2>
            <p className="text-sm sm:text-base text-blue-100 mb-3">
              Get an interactive walkthrough of how RepMingle monetizes idle trainer capacity.
            </p>
            <button
              onClick={onStartDemo}
              className="w-full bg-white text-blue-600 font-bold py-2 sm:py-3 px-3 rounded-lg hover:bg-blue-50 transition-colors text-sm sm:text-base"
            >
              Start Interactive Demo →
            </button>
          </Section>

          <Section>
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3">Executive Summary</h2>
            <div className="bg-blue-50 border border-2 border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 space-y-3">
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                <strong className="text-blue-600">RepMingle</strong> monetizes trainer expertise through intelligent micro sessions using behavioral psychology and optimal timing.
              </p>
              <p className="text-sm sm:text-base text-gray-700">
                <strong>Core Insight:</strong> Gyms have 40-45% trainer idle capacity during off-peak hours. Members want access. Trainers need bookings.
              </p>
              <p className="text-sm sm:text-base text-gray-700">
                <strong className="text-green-600">Outcome:</strong> Zero-revenue time slots generate revenue. Trainers earn from idle hours. Members get instant expert access.
              </p>
            </div>
          </Section>

          <Section>
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3">Market Opportunity</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 md:gap-4">
              <div className="bg-green-50 border-2 border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-green-600 mb-2">
                  $<Counter end={45.6} suffix="B" />
                </div>
                <div className="text-xs sm:text-sm text-gray-600 font-semibold">Personal Training Market</div>
              </div>
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-purple-600 mb-2">
                  <Counter end={45} suffix="%" />
                </div>
                <div className="text-xs sm:text-sm text-gray-600 font-semibold">Off-Peak Idle Time</div>
              </div>
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-blue-600 mb-2">
                  $<Counter end={45} />
                </div>
                <div className="text-xs sm:text-sm text-gray-600 font-semibold">Session Price</div>
              </div>
            </div>
          </Section>

          <Section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6">
            <h2 className="text-base sm:text-lg md:text-xl font-bold mb-3">Future of Facility Services</h2>
            <button
              onClick={onDismiss}
              className="w-full bg-white text-blue-600 font-bold py-2 sm:py-3 px-3 rounded-lg hover:bg-blue-50 transition-colors text-sm sm:text-base"
            >
              Back to Login
            </button>
          </Section>
        </div>

        {/* FOOTER */}
        <div className="bg-gray-50 px-3 sm:px-6 py-3 sm:py-4 text-center text-xs text-gray-600 border-t border-gray-200">
          <p>© 2025 RepMingle. All rights reserved. | Patent Pending</p>
        </div>
      </div>
    </div>
  );
};

// Main Login Component
const Login = ({ onLogin }) => {
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);

  const ACCESS_PASSWORD = 'RepMingle2025!';

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    setTimeout(() => {
      if (password === ACCESS_PASSWORD) {
        setIsLoggedIn(true);
        onLogin(true);
        setError('');
      } else {
        setError('Incorrect password. Please try again.');
        setPassword('');
      }
      setIsLoading(false);
    }, 500);
  };

  if (isLoggedIn) {
    return (
      <InvestorWelcome 
        onDismiss={() => setIsLoggedIn(false)}
        onStartDemo={() => alert('Demo starting...')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-700 flex items-center justify-center p-3 sm:p-4 relative overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-64 sm:w-80 md:w-96 h-64 sm:h-80 md:h-96 bg-white rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute top-0 right-0 w-64 sm:w-80 md:w-96 h-64 sm:h-80 md:h-96 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-64 sm:w-80 md:w-96 h-64 sm:h-80 md:h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-black/5" style={{
        backgroundImage: 'linear-gradient(0deg, transparent 24%, rgba(255,255,255,.05) 25%, rgba(255,255,255,.05) 26%, transparent 27%, transparent 74%, rgba(255,255,255,.05) 75%, rgba(255,255,255,.05) 76%, transparent 77%, transparent), linear-gradient(90deg, transparent 24%, rgba(255,255,255,.05) 25%, rgba(255,255,255,.05) 26%, transparent 27%, transparent 74%, rgba(255,255,255,.05) 75%, rgba(255,255,255,.05) 76%, transparent 77%, transparent)',
        backgroundSize: '50px 50px'
      }}></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Brand Section */}
        <div className="text-center mb-6 sm:mb-8 md:mb-10 animate-fadeIn">
          <div className="flex items-center justify-center mb-4 sm:mb-6">
            <img 
              src="/logo.png" 
              alt="RepMingle Logo" 
              className="h-14 sm:h-16 md:h-20 w-auto drop-shadow-lg"
            />
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-1 sm:mb-2 drop-shadow-lg leading-tight">
            RepMingle
          </h1>
          <p className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-yellow-300 via-red-300 to-pink-300 bg-clip-text text-transparent mb-3 sm:mb-4">
            Stop Guessing! Start Training!
          </p>

          {/* Patent Badge */}
          <div className="flex justify-center mb-3 sm:mb-4">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-amber-300/40 rounded-lg px-2 sm:px-3 py-2">
              <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-amber-300 to-orange-400 rounded-full"></div>
              <div>
                <p className="text-xs font-bold text-white leading-tight">PATENT PENDING</p>
                <p className="text-xs text-blue-100">US 2025</p>
              </div>
            </div>
          </div>

          <p className="text-blue-100 text-sm sm:text-base md:text-lg font-semibold">
            Premium Investor Access
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-2xl p-5 sm:p-6 md:p-10 border border-white/20">
          {/* Card Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl mb-3 sm:mb-4 shadow-lg">
              <Lock size={28} className="text-blue-600 sm:w-8 sm:h-8" />
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-1 sm:mb-2">Secure Access</h2>
            <p className="text-xs sm:text-sm text-gray-600">Enter password to access portal</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Password Input */}
            <div>
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative group">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 sm:px-5 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all bg-gray-50/50 group-hover:border-gray-400"
                  autoFocus
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 animate-shake">
                <p className="text-red-600 text-xs sm:text-sm font-semibold flex items-center gap-2">
                  <span>⚠️</span>
                  <span>{error}</span>
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm sm:text-base font-bold py-2.5 sm:py-3 md:py-4 rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all shadow-lg hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Verifying...</span>
                </span>
              ) : (
                'Access Investor Portal'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-4 sm:my-6">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-gray-400 text-xs">or</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg sm:rounded-xl p-4 sm:p-5 md:p-6 border border-blue-100">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full mb-2 sm:mb-3">
                <Mail size={20} className="text-blue-600 sm:w-6 sm:h-6" />
              </div>
              <p className="text-xs sm:text-sm text-gray-700 mb-1 font-semibold">Need Access?</p>
              <p className="text-xs text-gray-600 mb-2 sm:mb-3">Request password via email:</p>
              <a 
                href="mailto:drozaltun@gmail.com" 
                className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg text-xs sm:text-sm"
              >
                drozaltun@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 sm:mt-8">
          <p className="text-white/80 text-xs sm:text-sm mb-1">© 2025 RepMingle. All rights reserved.</p>
          <p className="text-white/60 text-xs">Patent Pending • Investor Confidential</p>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        @keyframes zoomIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.8s ease-out;
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }

        .animate-zoomIn {
          animation: zoomIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Login;